import { DateTime } from "luxon";
import { useState } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { CurrencyInput } from "@/components/common/CurrencyInput";
import { FormErrorSummary } from "@/components/common/FormErrorSummary";
import { FormField } from "@/components/common/FormField";
import { NumericStepper } from "@/components/common/NumericStepper";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { Vending } from "@/features/vending/api/vendingApi";
import { useUpdateVending } from "@/features/vending/hooks/useVending";
import { getApiValidationErrors } from "@/lib/apiClient";
import { toLocalDateTime } from "@/lib/datetime";
import { parseLocalizedNumber } from "@/lib/format";
import { focusFirstField } from "@/lib/forms";
import { toErrorMessage } from "@/lib/swr";

/** <input type="datetime-local"> takes minutes, the API stores seconds. */
const toInputValue = (value: string) => DateTime.fromISO(value).toFormat("yyyy-MM-dd'T'HH:mm");

type VendingFieldErrors = {
  label?: string;
  vendedAt?: string;
  quantity?: string;
  price?: string;
};

const VENDING_FIELD_IDS = {
  label: "vending-label",
  vendedAt: "vending-date",
  quantity: "vending-quantity",
  price: "vending-price",
} as const;

export function EditVendingDialog({
  vending,
  currency,
  productName,
  onClose,
  onSaved,
}: {
  vending: Vending;
  currency: string;
  productName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(vending.label);
  const [vendedAt, setVendedAt] = useState(toInputValue(vending.vendedAt ?? vending.createdAt));
  const [quantity, setQuantity] = useState(String(vending.quantity));
  const [price, setPrice] = useState(String(vending.price));
  const [fieldErrors, setFieldErrors] = useState<VendingFieldErrors>({});
  const [formError, setFormError] = useState<string>();

  const { update, isUpdating } = useUpdateVending();
  const toast = useToast();
  const maxVendedAt = DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm");

  const clearFieldError = (field: keyof VendingFieldErrors) => {
    setFieldErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    setFormError(undefined);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const errors: VendingFieldErrors = {};
    if (!(parseFloat(quantity) > 0)) errors.quantity = "Indiquez une quantité supérieure à 0.";
    if (!(parseLocalizedNumber(price) > 0)) errors.price = "Indiquez un prix supérieur à 0.";
    const parsedDate = DateTime.fromISO(vendedAt);
    if (!parsedDate.isValid || parsedDate > DateTime.now())
      errors.vendedAt = "La date de vente ne peut pas être dans le futur.";

    setFieldErrors(errors);
    setFormError(Object.keys(errors).length > 0 ? "Corrigez les champs signalés avant d’enregistrer." : undefined);
    const invalidIds = (Object.keys(VENDING_FIELD_IDS) as Array<keyof VendingFieldErrors>)
      .filter((field) => errors[field])
      .map((field) => VENDING_FIELD_IDS[field]);
    focusFirstField(invalidIds);
    if (Object.keys(errors).length > 0) {
      toast.error(Object.values(errors)[0] ?? "Corrigez les champs signalés.");
      return;
    }

    try {
      await update(vending.id, {
        label: label.trim() || `Vente de ${parsedDate.toFormat("dd/MM/yyyy")}`,
        vendedAt: toLocalDateTime(parsedDate),
        quantity: parseFloat(quantity),
        price: parseLocalizedNumber(price),
      });
      toast.success("Vente corrigée.");
      onSaved();
    } catch (e) {
      const apiErrors = getApiValidationErrors(e);
      const nextErrors: VendingFieldErrors = {
        label: apiErrors.label,
        vendedAt: apiErrors.vendedAt,
        quantity: apiErrors.quantity,
        price: apiErrors.price,
      };
      const fieldErrorIds = Object.entries(nextErrors)
        .filter(([, message]) => Boolean(message))
        .map(([field]) => VENDING_FIELD_IDS[field as keyof typeof VENDING_FIELD_IDS]);
      setFieldErrors(nextErrors);
      setFormError(
        fieldErrorIds.length > 0
          ? "Corrigez les champs signalés avant d’enregistrer."
          : (toErrorMessage(e) ?? undefined),
      );
      focusFirstField(fieldErrorIds);
      toast.error(toErrorMessage(e) ?? "La vente n’a pas pu être corrigée.");
    }
  };

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && !isUpdating && onClose()}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Corriger la vente</DialogTitle>
            <DialogDescription>{productName}</DialogDescription>
          </DialogHeader>

          <FormErrorSummary
            fields={[
              { id: VENDING_FIELD_IDS.label, label: "Libellé", message: fieldErrors.label },
              { id: VENDING_FIELD_IDS.vendedAt, label: "Date de la vente", message: fieldErrors.vendedAt },
              { id: VENDING_FIELD_IDS.quantity, label: "Quantité", message: fieldErrors.quantity },
              { id: VENDING_FIELD_IDS.price, label: "Prix encaissé", message: fieldErrors.price },
            ]}
          >
            {formError}
          </FormErrorSummary>

          <FormField
            label="Libellé"
            htmlFor={VENDING_FIELD_IDS.label}
            error={fieldErrors.label}
            errorId={`${VENDING_FIELD_IDS.label}-error`}
          >
            <Input
              id={VENDING_FIELD_IDS.label}
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                clearFieldError("label");
              }}
              aria-invalid={Boolean(fieldErrors.label)}
              aria-describedby={fieldErrors.label ? `${VENDING_FIELD_IDS.label}-error` : undefined}
              autoFocus
            />
          </FormField>

          <FormField
            label="Date de la vente"
            htmlFor={VENDING_FIELD_IDS.vendedAt}
            error={fieldErrors.vendedAt}
            errorId={`${VENDING_FIELD_IDS.vendedAt}-error`}
          >
            <Input
              id={VENDING_FIELD_IDS.vendedAt}
              type="datetime-local"
              value={vendedAt}
              max={maxVendedAt}
              onChange={(e) => {
                setVendedAt(e.target.value);
                clearFieldError("vendedAt");
              }}
              aria-invalid={Boolean(fieldErrors.vendedAt)}
              aria-describedby={fieldErrors.vendedAt ? `${VENDING_FIELD_IDS.vendedAt}-error` : undefined}
            />
          </FormField>

          <FormField
            label="Quantité"
            htmlFor={VENDING_FIELD_IDS.quantity}
            error={fieldErrors.quantity}
            errorId={`${VENDING_FIELD_IDS.quantity}-error`}
          >
            <NumericStepper
              id={VENDING_FIELD_IDS.quantity}
              step={0.01}
              min={0}
              value={quantity}
              onChange={(value) => {
                setQuantity(String(value));
                clearFieldError("quantity");
              }}
              aria-invalid={Boolean(fieldErrors.quantity)}
              aria-describedby={fieldErrors.quantity ? `${VENDING_FIELD_IDS.quantity}-error` : undefined}
            />
          </FormField>

          <FormField
            label="Prix encaissé"
            hint="Le solde du compte suit la différence."
            error={fieldErrors.price}
            htmlFor={VENDING_FIELD_IDS.price}
            errorId={`${VENDING_FIELD_IDS.price}-error`}
          >
            <CurrencyInput
              id={VENDING_FIELD_IDS.price}
              value={price}
              onValueChange={(value) => {
                setPrice(value);
                clearFieldError("price");
              }}
              currency={currency}
              className="font-semibold"
              aria-invalid={Boolean(fieldErrors.price)}
              aria-describedby={fieldErrors.price ? `${VENDING_FIELD_IDS.price}-error` : undefined}
            />
          </FormField>

          <DialogFooter className="gap-3 sm:gap-2">
            <Button type="button" variant="outline" size="lg" onClick={onClose} disabled={isUpdating}>
              Annuler
            </Button>
            <Button type="submit" size="lg" disabled={isUpdating}>
              {isUpdating ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
